import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave } from '@/ui/services/ServicesContext';
import shell from './CatalogSheet.module.css';
import styles from './MenuSheet.module.css';
import { useBack } from '@/ui/back/backStack';
import { PlayerAvatar } from '@/ui/components/PlayerAvatar';
import { useServices } from '@/ui/services/ServicesContext';
import { Icon, type IconName } from '@/ui/components/Icon';

export type MenuTarget = 'album' | 'achievements' | 'mail' | 'settings' | 'credits' | 'photo' | 'profile';

/** Painted icon for each Menu tile (gaps B4). */
const MENU_ICON: Record<MenuTarget, IconName> = {
  profile: 'profile',
  album: 'album',
  achievements: 'achievements',
  photo: 'camera',
  mail: 'mail',
  settings: 'settings',
  credits: 'credits',
};

/** S16 Menu (ux-screens §3.16). Entries appear as their features arrive. */
export function MenuSheet({
  onClose,
  onOpen,
  onBoss,
}: {
  onClose: () => void;
  onOpen: (target: MenuTarget) => void;
  /** Only when "Sếp tới!" is switched on in Settings. */
  onBoss?: () => void;
}) {
  useBack(onClose);
  const { t } = useI18n();
  const savedAt = useSave((s) => s.updatedAt);
  const claimable = useSave((s) =>
    Object.values(s.achievements).some((a) => a.completedAtPlayMs != null && a.claimedAtPlayMs == null),
  );
  const mail = useSave((s) => s.mailbox.pending.length + s.mailbox.special.length);
  const photoMode = useSave((s) => s.features.includes('photoMode'));
  const { content } = useServices();
  const titleId = useSave((s) => s.cosmetics.equippedTitle);
  const playerName = useSave((s) => s.avatar.name);
  const titleKey = content.cosmetics.titles.find((x) => x.id === titleId)?.nameKey;
  const items: { id: MenuTarget; label: string; dot?: boolean; count?: number }[] = [
    { id: 'album', label: t('ui.album.title') },
    { id: 'achievements', label: t('ui.menu.achievements'), dot: claimable },
    ...(photoMode ? [{ id: 'photo' as const, label: t('ui.photo.title') }] : []),
    { id: 'mail', label: t('ui.mail.title'), count: mail },
    { id: 'settings', label: t('ui.menu.settings') },
    { id: 'credits', label: t('ui.menu.credits') },
  ];
  return (
    <div
      className={shell.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`${shell.sheet} ${styles.menu}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('ui.menu.title')}
      >
        <div className={shell.grip} aria-hidden="true" />
        <header className={shell.header}>
          <h2 className={shell.title}>{t('ui.menu.title')}</h2>
          <button
            type="button"
            className={shell.iconBtn}
            aria-label={t('ui.catalog.close')}
            onClick={onClose}
          >
            <Icon name="close" size={26} />
          </button>
        </header>
        <button
          type="button"
          className={styles.profile}
          data-testid="menu-profile"
          onClick={() => {
            onOpen('profile');
          }}
        >
          <PlayerAvatar size={52} />
          <span className={styles.profileText}>
            <span className={styles.profileName}>{playerName ?? t('ui.profile.you')}</span>
            <span className={styles.profileTitle}>{titleKey ? t(titleKey) : t('ui.profile.edit')}</span>
          </span>
        </button>
        <p className={styles.saved}>{t('ui.settings.saved_at', { time: new Date(savedAt) })}</p>
        <div className={styles.grid}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.tile}
              data-testid={`menu-${item.id}`}
              onClick={() => {
                onOpen(item.id);
              }}
            >
              <Icon name={MENU_ICON[item.id]} size={34} />
              <span>{item.label}</span>
              {item.dot && <span className={styles.dot} aria-label={t('ui.tasks.claimable')} />}
              {item.count !== undefined && item.count > 0 && (
                <span className={styles.count}>{item.count}</span>
              )}
            </button>
          ))}
          {onBoss && (
            <button type="button" className={styles.tile} data-testid="menu-boss" onClick={onBoss}>
              <Icon name="boss" size={34} />
              <span>{t('ui.boss.button')}</span>
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
